import { useContext, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { MerchantContext, getToken } from 'commons-ui';
import config from 'config';

/**
 * Custom API client for Config Impact module.
 *
 * Bypasses commons-ui AjaxContext so we can route API calls to a
 * different stage env namespace (chirag-jain) without affecting other
 * modules or crm-ui, which continue to use the shared AjaxContext default.
 *
 * To change the namespace later, update STAGE_ENV_ID below.
 */
const STAGE_ENV_ID = 'chirag-jain';

const DEFAULT_REQUEST_TIMEOUT = 120000;

const buildHeaders = (merchantName: string) => {
	const token: any = getToken();
	const authToken = token?.accessToken?.accessToken;
	return {
		accessToken: authToken,
		'Content-Type': 'application/json',
		userMerchant: merchantName,
		'x-zupee-env': 'cse',
		'z-stage-env-id': STAGE_ENV_ID
	};
};

interface PostDataParams {
	endpoint: string;
	body?: any;
	queryParams?: any;
	timeout?: number;
}

const useApiClient = () => {
	const { merchantName } = useContext(MerchantContext);
	const history = useHistory();

	const postData = useCallback(
		({ endpoint, body = {}, queryParams = {}, timeout = DEFAULT_REQUEST_TIMEOUT }: PostDataParams) => {
			return axios({
				baseURL: config.ADMIN_API_URL,
				method: 'POST',
				url: endpoint,
				params: queryParams,
				headers: buildHeaders(merchantName),
				data: body,
				timeout
			})
				.then((response: any) => response)
				.catch((err: any) => {
					const statusCode = err?.response?.status || 405;
					if (statusCode === 500 || statusCode === 501 || statusCode === 400) {
						throw err;
					}
					if (statusCode === 403) {
						// Match commons-ui local-dev behavior — don't redirect, just throw
						// eslint-disable-next-line no-console
						console.warn('403 - Unauthorized:', endpoint);
						throw err;
					}
					history.push('/error', {
						statusCode,
						errorMsg: err?.response?.data?.error || err?.response?.statusText || 'Something went wrong'
					});
				});
		},
		[merchantName, history]
	);

	return { postData };
};

export default useApiClient;
